import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { PlatformLinkTypeViewStore } from "../../store";
import { PlatformLinkTypeView } from "../view/PlatformLinkTypeView";
import { PlatformLinkTypeUpdateStore } from "../../store";
import { PlatformLinkTypeUpdate } from "../update/PlatformLinkTypeUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type PlatformLinkTypeModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const PlatformLinkTypeModalContent: React.FC<PlatformLinkTypeModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new PlatformLinkTypeUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new PlatformLinkTypeViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <PlatformLinkTypeView
            platformLinkTypeViewStore={
              storeInfo.store as PlatformLinkTypeViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <PlatformLinkTypeUpdate
            platformLinkTypeUpdateStore={
              storeInfo.store as PlatformLinkTypeUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
